import { useState } from "react";
import { toast } from "sonner";
import { Building2, Upload, CheckCircle2, Loader2, ShieldCheck, X, ArrowRight, ArrowLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const DOC_TYPES = [
  { id: "modelo_036", label: "Modelo 036" },
  { id: "iae", label: "IAE" },
  { id: "otro", label: "Otro documento acreditativo" },
];
const ALLOWED = [".pdf", ".jpg", ".jpeg", ".png"];
const MAX_BYTES = 2 * 1024 * 1024;

const Label = ({ children, required }) => (
  <label className="font-mono text-[10px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">
    {children}{required && <span className="text-[#D95A2B]"> *</span>}
  </label>
);

const Field = ({ label, required, children, hint }) => (
  <div>
    <Label required={required}>{label}</Label>
    <div className="mt-1">{children}</div>
    {hint && <span className="block text-[11px] text-[#8A9098] mt-1">{hint}</span>}
  </div>
);

function AltaModal({ onClose }) {
  const [f, setF] = useState({
    company_name: "", commercial_name: "", tax_id: "", fiscal_address: "", postal_code: "",
    city: "", province: "", country: "", contact_first_name: "", contact_last_name: "",
    phone: "", email: "", role: "", website: "", business_activity: "", notes: "",
    document_type: "modelo_036", document_type_other: "",
  });
  const [files, setFiles] = useState([]);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onFiles = (list) => {
    const arr = Array.from(list || []);
    for (const file of arr) {
      const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED.includes(ext)) { toast.error(`Formato no permitido: ${file.name}. Usa PDF, JPG o PNG.`); return; }
      if (file.size > MAX_BYTES) { toast.error(`${file.name} supera el tamaño máximo permitido de 2 MB.`); return; }
    }
    setFiles(arr);
  };

  const submit = async (e) => {
    e.preventDefault();
    const req = [
      ["company_name", "La razón social es obligatoria."],
      ["tax_id", "El NIF/CIF es obligatorio."],
      ["fiscal_address", "La dirección fiscal es obligatoria."],
      ["postal_code", "El código postal es obligatorio."],
      ["city", "La población es obligatoria."],
      ["province", "La provincia es obligatoria."],
      ["country", "El país es obligatorio."],
      ["phone", "El teléfono es obligatorio."],
      ["contact_first_name", "El nombre de contacto es obligatorio."],
    ];
    for (const [k, msg] of req) { if (!f[k].trim()) return toast.error(msg); }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) return toast.error("Introduce un email válido.");
    if (f.document_type === "otro" && !f.document_type_other.trim()) return toast.error("Describe el documento acreditativo.");
    if (files.length === 0) return toast.error("Adjunta el modelo 036 o documentación del IAE.");
    if (!consent) return toast.error("Debes aceptar la política de privacidad.");
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v ?? ""));
      fd.append("consent", "true");
      files.forEach((file) => fd.append("files", file));
      const res = await fetch(`${API}/api/public/b2b/apply`, { method: "POST", body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "No se pudo enviar la solicitud.");
      setDone(true);
    } catch (err) {
      toast.error(err.message || "Error al enviar la solicitud.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/45 backdrop-blur-sm flex items-start justify-center p-0 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-2xl rounded-none sm:rounded-2xl border border-[#E5E5E0] my-0 sm:my-6 min-h-screen sm:min-h-0"
        style={{ boxShadow: "0 20px 50px rgba(15,18,16,.22)" }} onClick={(e) => e.stopPropagation()} data-testid="b2b-alta-modal">
        <div className="px-5 py-4 border-b border-[#E5E5E0] flex items-start justify-between sticky top-0 bg-white z-10 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <span className="ds-ic-tile"><Building2 size={15} /></span>
            <div>
              <h2 className="font-display font-bold text-base leading-tight">Solicitud de alta B2B</h2>
              <p className="text-[13px] text-[#4B5158] mt-0.5">Completa tus datos para solicitar acceso como cliente profesional.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#F0F0EB] text-[#525252] shrink-0" data-testid="b2b-alta-close" aria-label="Cerrar"><X size={18} /></button>
        </div>

        {done ? (
          <div className="p-8 text-center" data-testid="b2b-success">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#EFF3E6] mb-4">
              <CheckCircle2 size={30} className="text-[#6E8B3D]" />
            </div>
            <h3 className="font-display text-xl font-bold mb-2">Solicitud recibida</h3>
            <p className="text-sm text-[#4B5158] leading-relaxed max-w-md mx-auto">
              Hemos recibido tu solicitud de alta B2B. Revisaremos los datos y la documentación aportada
              y te informaremos por email cuando haya sido validada.
            </p>
            <button onClick={onClose} data-testid="b2b-back-login" className="ds-btn mt-6 justify-center">
              <ArrowLeft size={15} /> Volver al acceso B2B
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-6" data-testid="b2b-form">
            <div>
              <h3 className="font-mono text-[10px] uppercase tracking-[.14em] text-[#3F5222] font-semibold mb-3">Datos de empresa</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Razón social" required><input data-testid="b2b-company" className="ds-input" value={f.company_name} onChange={(e) => set("company_name", e.target.value)} /></Field>
                <Field label="Nombre comercial"><input className="ds-input" value={f.commercial_name} onChange={(e) => set("commercial_name", e.target.value)} /></Field>
                <Field label="NIF / CIF" required><input data-testid="b2b-taxid" className="ds-input mono" value={f.tax_id} onChange={(e) => set("tax_id", e.target.value)} /></Field>
                <Field label="País" required><input data-testid="b2b-country" className="ds-input" value={f.country} onChange={(e) => set("country", e.target.value)} /></Field>
                <div className="sm:col-span-2"><Field label="Dirección fiscal" required><input data-testid="b2b-address" className="ds-input" value={f.fiscal_address} onChange={(e) => set("fiscal_address", e.target.value)} /></Field></div>
                <Field label="Código postal" required><input data-testid="b2b-postal" className="ds-input" value={f.postal_code} onChange={(e) => set("postal_code", e.target.value)} /></Field>
                <Field label="Población" required><input data-testid="b2b-city" className="ds-input" value={f.city} onChange={(e) => set("city", e.target.value)} /></Field>
                <Field label="Provincia" required><input data-testid="b2b-province" className="ds-input" value={f.province} onChange={(e) => set("province", e.target.value)} /></Field>
              </div>
            </div>
            <div>
              <h3 className="font-mono text-[10px] uppercase tracking-[.14em] text-[#3F5222] font-semibold mb-3">Datos de contacto</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Nombre" required><input data-testid="b2b-contact-name" className="ds-input" value={f.contact_first_name} onChange={(e) => set("contact_first_name", e.target.value)} /></Field>
                <Field label="Apellidos"><input className="ds-input" value={f.contact_last_name} onChange={(e) => set("contact_last_name", e.target.value)} /></Field>
                <Field label="Teléfono" required><input data-testid="b2b-phone" className="ds-input" value={f.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
                <Field label="Email" required><input data-testid="b2b-email" type="email" className="ds-input" value={f.email} onChange={(e) => set("email", e.target.value)} /></Field>
                <div className="sm:col-span-2"><Field label="Cargo / Departamento"><input className="ds-input" value={f.role} onChange={(e) => set("role", e.target.value)} /></Field></div>
              </div>
            </div>
            <div>
              <h3 className="font-mono text-[10px] uppercase tracking-[.14em] text-[#3F5222] font-semibold mb-3">Información empresarial</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Página web"><input className="ds-input" value={f.website} onChange={(e) => set("website", e.target.value)} /></Field>
                <Field label="Actividad de la empresa"><input className="ds-input" value={f.business_activity} onChange={(e) => set("business_activity", e.target.value)} /></Field>
                <div className="sm:col-span-2"><Field label="Observaciones"><textarea className="ds-textarea" rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Field></div>
              </div>
            </div>
            <div>
              <h3 className="font-mono text-[10px] uppercase tracking-[.14em] text-[#3F5222] font-semibold mb-1">Documentación acreditativa</h3>
              <p className="text-[13px] text-[#4B5158] mb-3">Adjunta el modelo 036 o documentación del IAE para verificar tu actividad empresarial.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Tipo de documento" required>
                  <select data-testid="b2b-doctype" className="ds-select" value={f.document_type} onChange={(e) => set("document_type", e.target.value)}>
                    {DOC_TYPES.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                  </select>
                </Field>
                {f.document_type === "otro" && (
                  <Field label="Descripción del documento" required>
                    <input data-testid="b2b-docother" className="ds-input" value={f.document_type_other} onChange={(e) => set("document_type_other", e.target.value)} />
                  </Field>
                )}
              </div>
              <div className="mt-3">
                <Field label="Archivo (PDF, JPG o PNG · máx 2 MB)" required hint={files.length ? `${files.length} archivo(s): ${files.map((x) => x.name).join(", ")}` : "Puedes adjuntar uno o varios archivos."}>
                  <label className="flex flex-col items-center justify-center gap-2 border border-dashed border-[#D5D7CE] rounded-xl bg-[#FBFBF8] px-4 py-6 cursor-pointer hover:border-[#8AAF4E] hover:bg-[#F6F9EF] transition-colors text-center">
                    <Upload size={20} className="text-[#6E8B3D]" />
                    <span className="text-sm text-[#4B5158]">{files.length ? `${files.length} archivo(s) seleccionado(s)` : "Haz clic para adjuntar o arrastra tus archivos aquí"}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[.12em] text-[#8A9098]">PDF · JPG · PNG · máx 2 MB</span>
                    <input data-testid="b2b-files" type="file" multiple accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                      onChange={(e) => onFiles(e.target.files)} className="hidden" />
                  </label>
                </Field>
              </div>
            </div>
            <label className="flex items-start gap-3 text-sm cursor-pointer">
              <input data-testid="b2b-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 w-4 h-4 shrink-0 accent-[#6E8B3D]" />
              <span className="text-[#4B5158]">He leído y acepto la política de privacidad y autorizo el tratamiento de mis datos para gestionar mi solicitud de alta B2B.</span>
            </label>
            <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-3 pt-1 border-t border-dashed border-[#D5D7CE]">
              <p className="hidden sm:flex items-center gap-1.5 text-[11px] text-[#8A9098] pt-3"><ShieldCheck size={13} /> Datos y documentos tratados de forma confidencial.</p>
              <button type="submit" disabled={submitting} data-testid="b2b-submit" className="ds-btn primary w-full sm:w-auto justify-center mt-3 sm:mt-0" style={{ padding: "12px 20px", fontSize: 14 }}>
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                {submitting ? "Enviando…" : "Enviar solicitud B2B"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function B2BPublic() {
  const [showAlta, setShowAlta] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return toast.error("Introduce tu email y contraseña.");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/public/b2b/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "No se pudo iniciar sesión.");
      toast.success("Acceso correcto.");
    } catch (err) {
      toast.error(err.message || "Error de acceso.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg)" }}>
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl text-white mb-4"
              style={{ background: "linear-gradient(160deg,#6E8B3D,#8AAF4E)", boxShadow: "inset 0 -2px 0 rgba(0,0,0,.15),0 1px 2px rgba(15,18,16,.08)" }}>
              <Building2 size={22} strokeWidth={2.2} />
            </div>
            <div className="font-mono text-[11px] uppercase tracking-[.14em] text-[#3F5222] font-semibold mb-1">
              <span className="text-[#8A9098]">//</span>&nbsp;&nbsp;Portal B2B
            </div>
            <h1 className="font-display text-3xl font-black tracking-tight">Acceso profesional</h1>
            <p className="text-sm text-[#4B5158] mt-1">Para clientes profesionales de iRepairs</p>
          </div>

          <form onSubmit={login} className="ds-card p-6 space-y-4" style={{ boxShadow: "var(--shadow-lg)" }} data-testid="b2b-login-form">
            <div>
              <Label>Email</Label>
              <input data-testid="b2b-login-email" type="email" className="mt-1 ds-input" style={{ padding: "10px 12px" }}
                value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
            </div>
            <div>
              <Label>Contraseña</Label>
              <input data-testid="b2b-login-password" type="password" className="mt-1 ds-input" style={{ padding: "10px 12px" }}
                value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
            </div>
            <button type="submit" disabled={loading} data-testid="b2b-login-submit" className="ds-btn primary w-full justify-center" style={{ padding: "12px 16px", fontSize: 14 }}>
              {loading ? <Loader2 size={16} className="animate-spin" /> : null}
              {loading ? "Comprobando…" : "Iniciar sesión"}
              {!loading && <ArrowRight size={15} />}
            </button>
            <div className="text-center">
              <button type="button" className="text-xs text-[#8A9098] hover:text-[#3F5222] underline" data-testid="b2b-forgot">
                ¿Has olvidado tu contraseña?
              </button>
            </div>
          </form>

          <div className="ds-card p-5 mt-4 text-center">
            <p className="text-sm text-[#4B5158] mb-3">¿Todavía no tienes una cuenta profesional?</p>
            <button onClick={() => setShowAlta(true)} data-testid="b2b-open-alta" className="ds-btn w-full justify-center">
              <Building2 size={15} /> Solicitar alta B2B
            </button>
          </div>
        </div>
      </div>
      <footer className="text-center py-5 text-[11px] text-[#8A9098] font-mono uppercase tracking-[.12em]">
        El acceso B2B requiere una cuenta aprobada y activa.
      </footer>

      {showAlta && <AltaModal onClose={() => setShowAlta(false)} />}
    </div>
  );
}
